import React, { useEffect, useMemo, useState } from 'react';
import styles from './index.module.less';
import { getArticleListData } from '@/lib/service';
import { EssayClassifies, HomeArticleModel } from '@/types/home';
import { useTranslation } from 'next-i18next';
import { CommonBaseContent } from '../Layouts/CommonBackContent';
import { useConfigProvider } from '@/context/ConfigProvider';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import useScreenSize from '@/lib/hooks/utils/useScreenSize';
import { isEmpty } from 'lodash-es';

const HomeFullItem = dynamic(() => import('../Home/HomeFullItem'), { ssr: true });
const CategoryTopContent = dynamic(() => import('./Header'), { ssr: true });

const CategoryContent = ({ data }: { data: any }) => {
  // const { bile } = useConfigProvider();
  const { t } = useTranslation('common');
  const { width } = useScreenSize();
  const articleListRes = data['articleList'];

  const [articleList, setArticleList] = useState<HomeArticleModel[]>(articleListRes?.data);
  const [articleNetList, setArticleNetList] = useState<HomeArticleModel[]>(articleListRes?.data);
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookList, setBookList] = useState<HomeArticleModel[]>(data['bookList']);
  const [totalSize, setTotalSize] = useState<number>(articleListRes?.total);
  const [pageNo, setPageNo] = useState<number>(2);
  const [classifies, setClassifies] = useState<EssayClassifies[]>(data['classifies']);
  const page_size = 10;
  const { isBlogWeb } = useConfigProvider();
  const router = useRouter();
  const { pathname, locale, query } = router;

  useEffect(() => {
    if (!isBlogWeb) {
      router.push('/');
    }
  }, []);

  const fetchMoreData = () => {
    if (loading) {
      return;
    }

    setLoading(true);
    const type = !query.classifies ? (classifies?.length > 0 ? classifies[0].k : '') : query.classifies;
    getArticleListData({ page_no: pageNo, page_size: page_size, type }, data['locale'])
      .then((data) => {
        setPageNo(pageNo + 1);
        setTotalSize(data.total);
        setArticleNetList(data.data);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(false);
    setPageNo(2);
    setClassifies(data['classifies']);
    setBookList(data['bookList']);
    const articleListRes = data['articleList'];
    setArticleList(articleListRes?.data);
    setArticleNetList(articleListRes?.data);
  }, [data]);

  useEffect(() => {
    if (articleNetList?.length == 0) {
      setNoData(articleList?.length >= totalSize);
      return;
    }

    const tempIdList = articleList?.map((item) => {
      return item?.article_id;
    });

    const tempList = articleNetList?.filter((item): boolean => {
      return !tempIdList.includes(item?.article_id);
    });

    if (tempList?.length > 0) {
      setArticleList([...articleList, ...tempList]);
    }
  }, [articleNetList]);

  useEffect(() => {
    setNoData(totalSize != 0 && articleList?.length >= totalSize);
  }, [totalSize, articleList]);

  const getTopContent = useMemo(() => {
    return (
      <>
        {!!bookList.length && bookList.every((v) => v.cover_image) ? (
          <CategoryTopContent list={bookList} />
        ) : (
          <img
            style={{ width: '100%', height: 'auto', marginBottom: '50px' }}
            src="/images/article/heroImage.webp"
            alt=""
          />
        )}
      </>
    );
  }, [bookList]);

  const getTopCategoryContent = useMemo(() => {
    const tempClassifies = !query.classifies ? (classifies?.length > 0 ? classifies[0].k : '') : query.classifies;

    return (
      <div className={styles.utilsTop}>
        <div className={styles.taxonomicGroup}>
          {width <= 800 ? (
            <Select
              value={tempClassifies}
              placeholder={t('classify')}
              className={styles.selectModel}
              options={[
                // { value: 0, label: t('All') },
                ...classifies?.map((item) => ({
                  value: item.k,
                  label: item.v,
                })),
              ]}
              onChange={(val) => {
                handleChangeClassify(val + '');
              }}
            />
          ) : (
            <>
              {classifies?.map((item) => (
                <div
                  key={item.k}
                  onClick={() => handleChangeClassify(item.k)}
                  className={`${styles.classifyBtn} ${tempClassifies === item.k && styles.activeBtn}`}
                >
                  {item.v}
                </div>
              ))}
            </>
          )}
        </div>

        <Input
          className={styles.searchInput}
          style={{ borderRadius: '42px', width: '150px', height: '42px', transition: 'all .3s ' }}
          placeholder={t('search')}
          // variant="filled"
          suffix={<SearchOutlined />}
          onFocus={(e) => {
            e.target.parentElement.style.width = '200px';
          }}
          onBlur={(e) => {
            e.target.parentElement.style.width = '150px';
          }}
          onKeyDown={(key) => {
            if (key.key !== 'Enter') return;
            const inputValue = (key.target as HTMLInputElement).value;
            if (!inputValue) return;
            router.push(`/search?keyword=${inputValue || ''}`);
          }}
        />
      </div>
    );
  }, [width, query.classifies]);

  const getListContent = useMemo(() => {
    return (
      <div className={styles.leftContainer}>
        {isEmpty(articleList) ? (
          <>
            <HomeFullItem />
            <HomeFullItem />
            <HomeFullItem />
            <HomeFullItem />
          </>
        ) : (
          <>
            {(articleList ?? []).map((item, index) => {
              return (
                <HomeFullItem
                  key={item.article_id}
                  articleModel={item}
                  showBottomLine={index != articleList.length - 1}
                />
              );
            })}

            {!noData && (
              <div className={styles.loadMoreContent}>
                <div className={styles.loadMore} onClick={() => fetchMoreData()}>
                  <div className={styles.loadMoreBtn}>{loading ? `${t('loading')}...` : t('loadMore')}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }, [articleList, loading]);

  const handleChangeClassify = (k: string) => {
    let value = k === '0' || !k ? '' : k;
    const language = locale === 'es' ? '' : `/${locale}`;
    router.push({ pathname: `${language}${pathname}`, query: { classifies: value } });
  };

  if (!isBlogWeb) {
    return <></>;
  }

  return (
    <CommonBaseContent style={{ maxWidth: '1060px', minHeight: '100vh' }}>
      {getTopCategoryContent}

      <div className={styles.homeContent}>
        {getTopContent}

        <div className={styles.lineContainer}>
          <span>{t('latest')}</span>
        </div>

        {getListContent}
      </div>
    </CommonBaseContent>
  );
};

export default CategoryContent;
